import { useState, type ReactNode } from 'react'
import { useIsDesktop } from '@/ui'

/** Click-to-sort for the tables of 사용량. One place, so the tables cannot
 *  drift apart: the arrow means the same thing, a second click flips the same
 *  way, and a fresh column opens on the end that answers the question. Sorting
 *  is done in the client because every table already has its whole window in
 *  memory. */
export type SortDir = 'asc' | 'desc'
export type SortState<K extends string> = { key: K; dir: SortDir }

export function useTableSort<K extends string>(
  initial: SortState<K>,
  /** Columns that read as text, which open A-Z rather than largest-first. */
  textKeys: readonly K[] = [],
) {
  const [sort, setSort] = useState<SortState<K>>(initial)
  const pick = (key: K) =>
    setSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: textKeys.includes(key) ? 'asc' : 'desc' },
    )
  return { sort, pick }
}

/** Orders rows by what `value` returns for the active column. `tiebreak` keeps
 *  equal rows in a fixed order, so they do not shuffle on every refetch. */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: SortState<K>,
  value: (row: T, key: K) => number | string,
  tiebreak: (row: T) => string,
): T[] {
  const sign = sort.dir === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    const av = value(a, sort.key)
    const bv = value(b, sort.key)
    const d = typeof av === 'string' && typeof bv === 'string' ? av.localeCompare(bv) : Number(av) - Number(bv)
    if (d === 0) return tiebreak(a).localeCompare(tiebreak(b))
    return d * sign
  })
}

/** The label inside a sortable header. An inactive column shows no arrow (the
 *  space is kept so the header does not jump). On a phone the header is a
 *  line label inside a card, not a control, so it is plain text there and the
 *  page offers a sort select instead. */
export function SortLabel<K extends string>({
  k,
  sort,
  onPick,
  title = '눌러서 정렬',
  children,
}: {
  k: K
  sort: SortState<K>
  onPick: (k: K) => void
  title?: string
  children: ReactNode
}) {
  const desktop = useIsDesktop()
  if (!desktop) return <>{children}</>
  const active = sort.key === k
  return (
    <button
      type="button"
      onClick={() => onPick(k)}
      title={title}
      aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined}
      className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground' : ''}`}
    >
      {children}
      <span aria-hidden className={active ? '' : 'invisible'}>
        {sort.dir === 'desc' ? '↓' : '↑'}
      </span>
    </button>
  )
}
